import React, { useState, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { ChartToggle, ChartType } from './ChartToggle';

interface DailyScan {
  date: string;
  count: number;
}

interface ScansOverTimeChartProps {
  data: DailyScan[];
  className?: string;
}

const chartConfig = {
  scans: {
    label: "Scans",
    color: "#101F1C",
  },
};

export function ScansOverTimeChart({ data, className = '' }: ScansOverTimeChartProps) {
  const [chartType, setChartType] = useState<ChartType>('line');

  // Format data for the chart
  const formattedData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    scans: item.count,
    fullDate: item.date,
  }));

  // Calculate stats
  const stats = useMemo(() => {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const peak = Math.max(...data.map(d => d.count), 0);
    const peakDay = data.find(d => d.count === peak);
    return { total, peak, peakDay };
  }, [data]);

  const renderChart = () => {
    const commonProps = {
      data: formattedData,
      margin: { top: 10, right: 10, left: 0, bottom: 0 },
    };

    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#9A8F85' }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#9A8F85' }}
            tickFormatter={(value) => value.toLocaleString()}
            width={35}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => [`Scans: ${value.toLocaleString()}`, ""]}
                labelFormatter={(label) => `Date: ${label}`}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="scans"
            stroke="#101F1C"
            strokeWidth={2}
            dot={{ fill: '#101F1C', strokeWidth: 2, r: 3 }}
            activeDot={{ r: 5, stroke: '#101F1C', strokeWidth: 2, fill: '#FFFFFF' }}
            animationBegin={200}
            animationDuration={1200}
            animationEasing="ease-out"
          />
        </LineChart>
      );
    }

    return (
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: '#9A8F85' }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: '#9A8F85' }}
          tickFormatter={(value) => value.toLocaleString()}
          width={35}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value) => [value.toLocaleString(), "Scans"]}
              labelFormatter={(label) => `Date: ${label}`}
            />
          }
        />
        <Bar
          dataKey="scans"
          fill="#101F1C"
          radius={[3, 3, 0, 0]}
          animationBegin={200}
          animationDuration={1200}
          animationEasing="ease-out"
        />
      </BarChart>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[--clinqr-text-primary] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[--clinqr-navy]" />
            Scans Over Time
          </h3>
        </div>
        <div className="flex items-center justify-center h-48 bg-[--clinqr-bg-subtle] rounded-lg border border-dashed border-[--clinqr-border]/10">
          <div className="text-center">
            <p className="text-sm text-[--clinqr-text-secondary]">No scan data available</p>
            <p className="text-xs text-[--clinqr-text-muted] mt-1">Data will appear here once scans are recorded</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[--clinqr-text-primary] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[--clinqr-navy]" />
            Scans Over Time
          </h3>
          <p className="text-xs text-[--clinqr-text-secondary]">
            {stats.total.toLocaleString()} total · Peak: {stats.peak}
          </p>
        </div>
        <ChartToggle chartType={chartType} onToggle={setChartType} />
      </div>

      <div className="w-full overflow-hidden">
        <ChartContainer config={chartConfig} className="h-48 sm:h-52 w-full">
          {renderChart()}
        </ChartContainer>
      </div>
    </div>
  );
}
